import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, RotateCcw, X } from 'lucide-react';

import { useT } from '@/contexts/I18nContext';
import { ImageLightbox, type LightboxCropSpec } from '@/components/ui/image-lightbox';
import { Combobox } from '@/components/ui/combobox';
import { cn } from '@/lib/utils';
import { DayPhotoStrip, STRIP_FOOTER_H, type DayStripPhoto } from './DayPhotoStrip';
import type { OrigGeom } from './OrigCropView';
import { useChartTouchTooltipGuard } from './chartTouchTooltipGuard';

// Fullscreen chart lightbox for the День report cards (Очередь / Выкладка).
//
// The point of fullscreen is the PHOTOS: the strip is much taller than the
// in-card one and the lightbox fetches its own denser frame set sized to its
// width (the whole width fills with frames), while the chart stays compact —
// it's already readable at card size. The operator can MARQUEE-SELECT a
// period on the chart to zoom the X domain in. Double-tap the chart or the
// «Сброс» button returns to the whole day. Built on pointer events (one path
// for mouse + touch), so the marquee works on phones too.

const CARD_IMG_H = 84; // px — DayPhotoStrip's default height, for slot scaling
const CHART_H = 240; // px — compact; the day is already readable at card size
const STRIP_ROW_GAP = 6;
// Everything around the strip: modal header + paddings + chart + hint,
// plus the strip's own footer (connector ladder + rail) and mobile scroll
// indicator — underestimating this reintroduces a sliver of body scroll.
const NON_STRIP_H = CHART_H + 208;
const DRAG_MIN_PX = 8; // below this a pointer-up is a tap, not a marquee
const MIN_SPAN_MS = 5 * 60 * 1000; // don't zoom tighter than 5 min
const DOUBLE_TAP_MS = 320;

/** Strip image height that fills the viewport's free height with `lanes`
 *  rows (the modal grows as tall as the screen allows), clamped to sane
 *  bounds. Computed once per open — fullscreen isn't resized mid-look. */
function stripImgH(lanes: number): number {
  const free = window.innerHeight * 0.94 - NON_STRIP_H;
  const perLane = Math.floor((free - (lanes - 1) * STRIP_ROW_GAP) / lanes);
  return Math.max(120, Math.min(perLane, lanes > 1 ? 230 : 280));
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

export interface DayChartLightboxProps {
  title: string;
  /** All strip photos (unfiltered). The lightbox filters to the zoom window. */
  photos: DayStripPhoto[];
  /** Caption for a photo in the image gallery. */
  captionOf: (p: DayStripPhoto) => string;
  /** Full-day X domain in ms [start, end]. */
  fullDomain: [number, number];
  /** Optional preset zoom window [start, end] to open at (e.g. when launched
   *  from a "Посмотреть" button on a specific problem period). Clamped to
   *  `fullDomain` by the caller. `null`/absent → opens at the full day. */
  initialZoom?: [number, number] | null;
  /** Per-image slot width on the CARD, paired with `cardImgH` — the lightbox
   *  scales the slot up to its taller strip while PRESERVING the card's slot
   *  aspect (slotW / cardImgH). */
  slotW?: number;
  /** The card's image height that `slotW` was chosen for (so the fullscreen
   *  slot keeps the same aspect). Defaults to DayPhotoStrip's 84px nominal. */
  cardImgH?: number;
  /** Plot-area insets (px) so pointer-x maps to time correctly: `left` = the
   *  chart's Y-axis width + left margin, `right` = the right margin,
   *  `bottom` = X-axis ticks + legend height, so the marquee stops at the
   *  x-axis instead of covering the legend. */
  plotInset: { left: number; right: number; bottom?: number };
  /** Extra X-domain padding the CHART renders beyond [domain] on each side
   *  (the queue card pads by half a bucket so edge bars aren't half-clipped).
   *  The strip's time axis and the marquee mapping pad by the same amount so
   *  photos stay glued to their chart x. */
  axisPadMs?: number;
  /** Render the chart for a given X domain + height. `fullscreen` lets the
   *  renderer adapt (e.g. crosshair cursor for the marquee instead of the
   *  in-card click-a-point pointer). */
  /** `zoomTo` — приблизить окно из самого графика (клик по участку, #3076). */
  renderChart: (opts: { domain: [number, number]; height: number; fullscreen?: boolean; zoomTo?: (z: [number, number]) => void }) => React.ReactNode;
  /** Optional loader for FRESH frames scoped to a window, sized to the
   *  lightbox's own width (`count`). Fired for the full day on open and for
   *  every zoom window — the fullscreen strip is denser than the card's, so
   *  merely reusing/filtering the card's set would leave the width unfilled.
   *  While it loads, the `photos` prop (filtered to the window) is shown. */
  loadWindowPhotos?: (domain: [number, number], count: number) => Promise<DayStripPhoto[]>;
  /** Identity of WHAT the lightbox shows (the rack id). When it changes the
   *  window-photo set is dropped and refetched — otherwise switching the rack
   *  in fullscreen kept the previous rack's dense strip forever (Alexey
   *  2026-09-08: «график сменился, на фото всё ещё готовая еда»). */
  contentKey?: string | number | null;
  /** The caller is still fetching the chart/photos for `contentKey` — show
   *  the loader instead of the stale previous content. */
  contentLoading?: boolean;
  /** Optional "все стеллажи" mode: swaps the body for a grid of per-rack mini
   *  charts (`content`) when `active`. The header control is a rack DROPDOWN
   *  with «Все» as its first item when `picker` is supplied (mirrors the card
   *  picker + «Все филиалы»); otherwise a plain «Все» toggle button. */
  allRacks?: {
    active: boolean;
    onToggle: () => void;
    label: string;
    content: React.ReactNode;
    picker?: {
      value: string;
      options: { value: string; label: string }[];
      onChange: (v: string) => void;
    };
  };
  /** Photo strip rows. Default 1 — one clean chronological line (Очередь,
   *  Выкладка). Столы passes 2: each incident is a multi-photo cluster, so
   *  the taller staggered strip earns its keep there. */
  stripLanes?: number;
  /** Passed straight to the strip: COVERAGE-first frame selection (even full-day
   *  spread + bounded incident swap). The rack «Выкладка» lightbox sets this so
   *  the afternoon isn't crowded out by opening-ramp dips. Queue leaves it off. */
  coverageFirst?: boolean;
  /** Fired when the marquee zoom window changes (null = back to full) — lets
   *  the caller refetch the chart series at a finer resolution. */
  onZoomChange?: (zoom: [number, number] | null) => void;
  /** «Фото в оригинальных пропорциях» (Алексей 26.10 #2638) — прямо в полосу фото. */
  aspectOverride?: number | null;
  origGeom?: OrigGeom | null;
  /** Лайтбокс фото (#2636/#2637): полный кадр с контуром (по удержанию при primary="crop")
   *  и кроп, вырезанный из кадра, — по фото; null → как раньше, сохранённый кроп. */
  lightboxFrameOf?: (p: DayStripPhoto) => {
    frameSrc: string | null;
    polygons: number[][][] | null;
    crop?: LightboxCropSpec | null;
  } | null;
  lightboxPrimary?: 'frame' | 'crop';
  holdHint?: string;
  onClose: () => void;
}

export function DayChartLightbox({
  title,
  photos,
  captionOf,
  fullDomain,
  initialZoom,
  slotW,
  cardImgH = CARD_IMG_H,
  plotInset,
  axisPadMs = 0,
  renderChart,
  loadWindowPhotos,
  contentKey = null,
  contentLoading = false,
  allRacks,
  stripLanes = 1,
  coverageFirst = false,
  onZoomChange,
  aspectOverride,
  origGeom,
  lightboxFrameOf,
  lightboxPrimary,
  holdHint,
  onClose,
}: DayChartLightboxProps) {
  const { t } = useT();
  const touchTooltipGuard = useChartTouchTooltipGuard();
  const [zoom, setZoom] = useState<[number, number] | null>(initialZoom ?? null);
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  // Let the caller react to zoom (refetch the series at finer resolution).
  const zoomChangeRef = useRef(onZoomChange);
  zoomChangeRef.current = onZoomChange;
  useEffect(() => {
    zoomChangeRef.current?.(zoom);
    // Keyed on the window bounds; the callback is read via a ref.
  }, [zoom?.[0], zoom?.[1]]);

  const domain: [number, number] = zoom ?? fullDomain;

  // Strip sizing: image height fills the viewport's free height across the
  // strip's rows; the nominal slot width scales with the height, and the
  // fit count (per lane) follows from the lightbox's own width.
  const [imgH] = useState(() => stripImgH(stripLanes));
  // Reserve the strip's area with a FIXED height computed from imgH — NOT a
  // measured one. Measuring the rendered strip and feeding it back into this
  // reservation created a measure→setState→re-measure loop (made worse by a
  // scrollbar toggling the width) that oscillated on fullscreen (Alexey
  // 2026-07-21). Rack/queue crops share one aspect, so the strip fills this
  // reserve exactly; a rare short crop just leaves a little slack, no bounce.
  const stripReserveH = stripLanes * imgH + (stripLanes - 1) * STRIP_ROW_GAP + STRIP_FOOTER_H;
  // Pre-load placeholder slot width at the fullscreen height. The strip re-sizes
  // every slot to the rack's real aspect once its first photo loads (823);
  // scaling the card's nominal slot keeps this reservation close so the one-time
  // width settle is small.
  const stripSlotW = Math.round((slotW ?? 96) * (imgH / cardImgH));

  // Freshly-loaded frames for the current window (when a loader is wired) —
  // fetched for the FULL day too, not only zoom windows, because the
  // fullscreen strip is denser than the card's. `null` → not loaded yet;
  // fall back to (filtered) `photos`.
  const [windowPhotos, setWindowPhotos] = useState<DayStripPhoto[] | null>(null);
  const [loadingWindow, setLoadingWindow] = useState(false);
  const loadRef = useRef(loadWindowPhotos);
  loadRef.current = loadWindowPhotos;
  const reqRef = useRef(0);
  const contentKeyRef = useRef<string | number | null>(contentKey);

  useEffect(() => {
    const load = loadRef.current;
    if (!load) {
      setWindowPhotos(null);
      setLoadingWindow(false);
      return;
    }
    const id = ++reqRef.current;
    setLoadingWindow(true);
    // NOTE: the previous window's photos stay on screen (filtered to the new
    // window below) until the fresh set lands — clearing them here made the
    // whole modal jump on every zoom. A CONTENT change (another rack) is the
    // exception: those photos belong to something else, drop them at once.
    if (contentKeyRef.current !== contentKey) {
      contentKeyRef.current = contentKey;
      setWindowPhotos(null);
    }
    // FIXED count — NOT derived from a measured width. The old fitCount-based
    // count churned as the modal width settled on open, refetching a different
    // set each time → «дикая тряска фотографий» on open and on marquee period-
    // select (Alexey 2026-07-21). A constant loads the window's set ONCE and it
    // stays put; the strip renders as many of these as fit. 48 is dense enough
    // to fill the wide two-lane fullscreen strip.
    load(zoom ?? fullDomain, 48)
      .then((ph) => { if (reqRef.current === id) setWindowPhotos(ph); })
      .catch(() => { if (reqRef.current === id) setWindowPhotos(null); })
      .finally(() => { if (reqRef.current === id) setLoadingWindow(false); });
    // Keyed ONLY on the window bounds (+ the content identity) — one fetch per
    // period, no width churn.
  }, [zoom?.[0], zoom?.[1], fullDomain[0], fullDomain[1], contentKey]);

  // Photos inside the current window (drives both the strip and the gallery, so
  // the strip zooms in lockstep with the chart). Prefer freshly-loaded dense
  // frames; while they load, the PREVIOUS set (window-filtered) stays as the
  // placeholder so nothing blinks out mid-zoom.
  const domainLo = domain[0];
  const domainHi = domain[1];
  const visible = useMemo(() => {
    if (contentLoading) return [];
    const base = windowPhotos && windowPhotos.length > 0 ? windowPhotos : photos;
    return base.filter((p) => {
      const t0 = new Date(p.at).getTime();
      return Number.isFinite(t0) && t0 >= domainLo && t0 <= domainHi;
    });
  }, [photos, windowPhotos, domainLo, domainHi, contentLoading]);

  // Gallery = EXACTLY the photos the strip renders (reported via
  // onVisiblePhotos); the strip drops some to fit, so building from the full
  // window set paged through more than were on screen (Alexey 2026-07-21).
  const [lbPhotos, setLbPhotos] = useState<DayStripPhoto[]>([]);
  const srcs = useMemo(() => lbPhotos.map((p) => p.image_url!).filter(Boolean), [lbPhotos]);
  const captions = useMemo(() => lbPhotos.map((p) => captionOf(p)), [lbPhotos, captionOf]);
  // Кадр/контур/кроп — по тем же фото, что и srcs (с image_url), индекс в индекс.
  const lbFrames = useMemo(
    () => (lightboxFrameOf ? lbPhotos.filter((p) => p.image_url).map((p) => lightboxFrameOf(p)) : null),
    [lbPhotos, lightboxFrameOf],
  );

  // ── Marquee state ──
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const dragStartX = useRef<number | null>(null);
  const lastTap = useRef(0);
  const [sel, setSel] = useState<{ a: number; b: number } | null>(null);
  // TRUE crosshair: recharts' cursor snaps to data and freezes at the last
  // point over empty regions (nights) — this line follows the REAL pointer
  // (direct DOM writes, no re-render per mousemove), so it's always clear
  // where a marquee will start.
  const crossRef = useRef<HTMLDivElement | null>(null);
  const moveCrosshair = (clientX: number | null) => {
    const el = crossRef.current;
    const box = overlayRef.current;
    if (!el || !box) return;
    if (clientX == null) {
      el.style.display = 'none';
      return;
    }
    const r = box.getBoundingClientRect();
    const x = clientX - r.left;
    if (x < plotInset.left || x > r.width - plotInset.right) {
      el.style.display = 'none';
      return;
    }
    el.style.display = 'block';
    el.style.left = `${x}px`;
  };

  const timeAt = (clientX: number): number => {
    const el = overlayRef.current;
    if (!el) return domain[0];
    const r = el.getBoundingClientRect();
    const left = r.left + plotInset.left;
    const right = r.right - plotInset.right;
    const frac = clamp((clientX - left) / Math.max(right - left, 1), 0, 1);
    // The chart's plot maps to the PADDED domain (see axisPadMs).
    const d0 = domain[0] - axisPadMs;
    const d1 = domain[1] + axisPadMs;
    return clamp(d0 + frac * (d1 - d0), domain[0], domain[1]);
  };
  const pxAt = (clientX: number): number => {
    const el = overlayRef.current;
    if (!el) return 0;
    const r = el.getBoundingClientRect();
    return clamp(clientX - r.left, 0, r.width);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (!e.isPrimary) return;
    // Below the plot (x-axis + legend zone): don't start a marquee and don't
    // capture the pointer — capturing swallowed the legend's toggle clicks
    // («Открытых касс» / «За баром» were unclickable in fullscreen).
    const r = overlayRef.current?.getBoundingClientRect();
    if (r && plotInset.bottom && e.clientY > r.bottom - plotInset.bottom) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragStartX.current = e.clientX;
    const x = pxAt(e.clientX);
    setSel({ a: x, b: x });
  };
  const onPointerMove = (e: React.PointerEvent) => {
    moveCrosshair(e.clientX);
    if (dragStartX.current == null) return;
    const x = pxAt(e.clientX);
    setSel((s) => (s ? { ...s, b: x } : { a: x, b: x }));
  };
  const onPointerLeave = () => moveCrosshair(null);
  const onPointerUp = (e: React.PointerEvent) => {
    const startX = dragStartX.current;
    dragStartX.current = null;
    setSel(null);
    if (startX == null) return;
    const dx = Math.abs(e.clientX - startX);
    if (dx < DRAG_MIN_PX) {
      // Tap. A second tap within the window resets the zoom (double-tap).
      const now = Date.now();
      if (now - lastTap.current < DOUBLE_TAP_MS) {
        setZoom(null);
        lastTap.current = 0;
      } else {
        lastTap.current = now;
      }
      return;
    }
    const t1 = timeAt(startX);
    const t2 = timeAt(e.clientX);
    const lo = Math.min(t1, t2);
    const hi = Math.max(t1, t2);
    if (hi - lo >= MIN_SPAN_MS) setZoom([lo, hi]);
  };
  const onPointerCancel = () => {
    dragStartX.current = null;
    setSel(null);
  };

  // The strip maps time over the same PADDED domain the chart renders.
  const workStartIso = new Date(domain[0] - axisPadMs).toISOString();
  const workEndIso = new Date(domain[1] + axisPadMs).toISOString();

  return (
    <>
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-2 sm:p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[94vh] w-full max-w-[1400px] flex-col overflow-hidden rounded-xl bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — the frames loader lives HERE so the body never jumps. */}
        <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-sm font-semibold text-foreground">{title}</span>
            {allRacks && (
              allRacks.picker ? (
                <div className="min-w-0 shrink-0 sm:min-w-40">
                  <Combobox
                    value={allRacks.picker.value}
                    onChange={allRacks.picker.onChange}
                    options={allRacks.picker.options}
                    className="h-7 rounded-full text-xs shadow-none"
                    id="lightbox-rack-picker"
                  />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={allRacks.onToggle}
                  aria-pressed={allRacks.active}
                  className={cn(
                    'inline-flex shrink-0 items-center rounded-md border px-2 py-0.5 text-xs font-medium transition-colors',
                    allRacks.active
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border text-muted-foreground hover:text-foreground',
                  )}
                >
                  {allRacks.label}
                </button>
              )
            )}
            {!allRacks?.active && zoom && (
            <button
              type="button"
              onClick={() => setZoom(null)}
              className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border px-2 py-0.5 text-xs font-medium text-muted-foreground hover:text-foreground"
            >
              <RotateCcw className="size-3" />
              {t('report.day.zoomReset')}
            </button>
            )}
            {!allRacks?.active && (loadingWindow || contentLoading) && (
              <span className="inline-flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
                <Loader2 className="size-3.5 animate-spin" />
                <span className="hidden sm:inline">{t('report.day.loadingFrames')}</span>
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label={t('common.close')}
          >
            <X className="size-5" />
          </button>
        </div>

        {/* Body — photos first (that's what fullscreen is for), compact chart.
            In "все стеллажи" mode the body is the per-rack chart grid instead. */}
        {allRacks?.active ? (
          // Touch guard: scrolling this grid of charts must not open (or
          // leave behind) tooltips; finger release always closes them.
          <div className="flex-1 overflow-y-auto p-4 [scrollbar-gutter:stable]" {...touchTooltipGuard}>{allRacks.content}</div>
        ) : (
        // scrollbar-gutter: stable — a scrollbar appearing must NOT change the
        // body width (that width shift re-lays-out the strip and oscillates).
        <div className="flex-1 space-y-3 overflow-y-auto p-4 [scrollbar-gutter:stable]" {...touchTooltipGuard}>
          {/* The strip area hugs the strip's ACTUAL rendered height (reported
              via onHeight). It must NOT reserve a fixed imgH-based height with
              justify-end: wide crops shrink effImgH below imgH after the images
              load, so a fixed reserved box left an empty BAND above the
              bottom-anchored strip (owner-reported: appears mid-load, photos
              slide down, bottom row clipped). Sizing to the real height — like
              the in-card strip, which never shows the band — kills it; effImgH
              ≤ imgH always, so this never exceeds the screen-fill target and the
              body never scroll-clips. Falls back to the nominal reserved height
              until the strip reports (and while it's empty, so the loader has
              room). */}
          {/* minHeight (not a fixed height): reserves room while the photos load
              so the chart doesn't jump, but then hugs the strip's own FIXED
              height exactly — no gap between the dots line and the chart (Alexey
              2026-07-22, 808b). The strip height is deterministic now (fixed
              slots), so this never oscillates. */}
          <div
            className="relative flex flex-col overflow-x-hidden"
            style={{ minHeight: stripReserveH }}
          >
            {visible.length > 0 ? (
              <DayPhotoStrip
                photos={visible}
                workStart={workStartIso}
                workEnd={workEndIso}
                onOpen={setOpenIdx}
                onVisiblePhotos={setLbPhotos}
                slotW={stripSlotW}
                imgH={imgH}
                lanes={stripLanes}
                coverageFirst={coverageFirst}
                aspectOverride={aspectOverride}
                origGeom={origGeom}
                // Fullscreen shows ≥10 — count wins over natural aspect; wide
                // crops shrink to fit rather than dropping to ~3 (847/848).
                minCount={10}
                axisInset={{ left: plotInset.left, right: plotInset.right }}
              />
            ) : (
              (loadingWindow || contentLoading) && (
                <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <Loader2 className="size-6 animate-spin text-muted-foreground" aria-hidden />
                </span>
              )
            )}
          </div>

          {/* Chart + marquee. Handlers live on the WRAPPER (not a covering
              overlay) so pointer events still reach the chart — the recharts
              crosshair/tooltip works while a drag draws the marquee, which
              stops at the x-axis (plotInset.bottom). */}
          <div
            ref={overlayRef}
            className="relative cursor-crosshair"
            style={{ touchAction: 'none' }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
            onPointerLeave={onPointerLeave}
          >
            {renderChart({
              domain, height: CHART_H, fullscreen: true,
              zoomTo: (z) => setZoom([Math.max(fullDomain[0], z[0]), Math.min(fullDomain[1], z[1])]),
            })}
            <div
              ref={crossRef}
              className="pointer-events-none absolute top-0 w-px bg-muted-foreground/40"
              style={{ display: 'none', bottom: plotInset.bottom ?? 0 }}
            />
            {sel && Math.abs(sel.b - sel.a) > 1 && (
              <div
                className="pointer-events-none absolute top-0 border-x border-primary bg-primary/15"
                style={{
                  left: Math.min(sel.a, sel.b),
                  width: Math.abs(sel.b - sel.a),
                  bottom: plotInset.bottom ?? 0,
                }}
              />
            )}
          </div>

          <p className="text-center text-xs text-muted-foreground">
            {/* «весь день» reads wrong when the lightbox covers a week. */}
            {t(
              fullDomain[1] - fullDomain[0] > 36 * 3600 * 1000
                ? 'report.day.zoomHintRange'
                : 'report.day.zoomHint',
            )}
          </p>
        </div>
        )}
      </div>
    </div>

      {openIdx != null && (
        <ImageLightbox
          srcs={srcs}
          index={openIdx}
          alt=""
          captions={captions}
          frameSrcs={lbFrames?.map((f) => f?.frameSrc ?? null)}
          polygonsList={lbFrames?.map((f) => f?.polygons ?? null)}
          cropSpecs={lbFrames?.map((f) => f?.crop ?? null)}
          primary={lightboxPrimary}
          holdHint={holdHint}
          onClose={() => setOpenIdx(null)}
          onNavigate={setOpenIdx}
        />
      )}
    </>
  );
}
